import React, { Fragment, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { Field } from '@atlaskit/form';
import type { ValueType, SingleValueProps, OptionProps } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import {
	useIsConfigureLoading,
	useProjectsActions,
	useProjectsState,
} from '../../providers/BulkConfigureContextProvider';
import type { JiraProject } from '../../__types__/apiUtils';
import { transformStargateUrl } from '../../utils/configureUtils';

import { BulkConfigureSelectOption } from './BulkConfigureSelectOption';

type ProjectOption = {
	label: string;
	value: JiraProject;
	avatarUrl: string;
};

const mapProjectToOption = (project: JiraProject): ProjectOption => {
	return {
		label: project.name,
		value: project,
		avatarUrl: transformStargateUrl(project.avatarUrls['48x48']),
	};
};

const ProjectTypeOption = (props: OptionProps<ProjectOption>) => (
	<components.Option {...props}>
		<BulkConfigureSelectOption label={props.label} avatarUrl={props.data.avatarUrl} />
	</components.Option>
);

const ProjectTypeSingleValue = (props: SingleValueProps<ProjectOption>) => (
	<components.SingleValue {...props}>
		<BulkConfigureSelectOption label={props.data.label} avatarUrl={props.data.avatarUrl} />
	</components.SingleValue>
);

// TODO: Remove optional props once we cleanup jira_ai_decouple_issue_create_fields FF
interface ProjectSelectorProps {
	optionsAllProjects?: JiraProject[];
	optionsRecentProjects?: JiraProject[];
	value?: JiraProject | null;
	onChange?: (project: JiraProject | null) => void;
	isLoading?: boolean;
	isDisabled?: boolean;
	onInputChange?: (query: string) => void;
	label?: string;
	labelProjectSelectorRecentProjects?: string;
	labelProjectSelectorProjects?: string;
}

// TODO: Remove Partial type once we cleanup jira_ai_decouple_issue_create_fields FF
export const ProjectSelector = (props: ProjectSelectorProps) => {
	const { formatMessage } = useIntl();
	const {
		selectedProject,
		optionsRecentProjects: recentProjects,
		optionsAllProjects: allProjects,
	} = useProjectsState();
	const { setSelectedProject, setProjectSelectorQuery } = useProjectsActions();
	const { isLoading: isConfigureLoading } = useIsConfigureLoading();

	// Old Sweet State logic
	const optionsSweetState = useMemo(() => {
		if (recentProjects && recentProjects.length > 0) {
			return [
				{
					label: formatMessage(messages.bulkConfigureFormProjectSelectorRecentProjects),
					options: recentProjects.map(mapProjectToOption),
				},
				{
					label: formatMessage(messages.bulkConfigureFormProjectSelectorProjects),
					options: allProjects?.map(mapProjectToOption) || [],
				},
			];
		}
		return [{ options: allProjects?.map(mapProjectToOption) || [] }];
	}, [recentProjects, allProjects, formatMessage]);

	const onChangeSweetState = useCallback(
		(selectedOption: ValueType<ProjectOption>) => {
			if (selectedOption && selectedOption.value !== selectedProject) {
				void setSelectedProject(selectedOption.value);
			}
		},
		[selectedProject, setSelectedProject],
	);

	const debounceTimeoutSweetStateRef = useRef<NodeJS.Timeout | null>(null);
	const onInputChangeSweetState = useCallback(
		(query: string) => {
			if (debounceTimeoutSweetStateRef.current) {
				clearTimeout(debounceTimeoutSweetStateRef.current);
			}
			debounceTimeoutSweetStateRef.current = setTimeout(() => {
				setProjectSelectorQuery(query);
			}, 300);
		},
		[setProjectSelectorQuery],
	);

	const selectedValueSweetState = selectedProject ? mapProjectToOption(selectedProject) : null;

	// New decoupled logic (props/local state)
	const {
		optionsAllProjects = [],
		optionsRecentProjects = [],
		value: controlledValue,
		onChange,
		isLoading = false,
		isDisabled = false,
		onInputChange,
		labelProjectSelectorRecentProjects,
		labelProjectSelectorProjects,
	} = props;

	const [localValue, setLocalValue] = useState<JiraProject | null>(controlledValue ?? null);
	const selectedValue = localValue ? mapProjectToOption(localValue) : null;

	const groupedOptions = useMemo(() => {
		if (optionsRecentProjects && optionsRecentProjects.length > 0) {
			return [
				{
					// TODO: Remove formatMessage once we cleanup jira_ai_decouple_issue_create_fields FF
					label:
						labelProjectSelectorRecentProjects ??
						formatMessage(messages.bulkConfigureFormProjectSelectorRecentProjects),
					options: optionsRecentProjects.map(mapProjectToOption),
				},
				{
					// TODO: Remove formatMessage once we cleanup jira_ai_decouple_issue_create_fields FF
					label:
						labelProjectSelectorProjects ??
						formatMessage(messages.bulkConfigureFormProjectSelectorProjects),
					options: optionsAllProjects?.map(mapProjectToOption) || [],
				},
			];
		}
		return [{ options: optionsAllProjects?.map(mapProjectToOption) || [] }];
	}, [
		optionsRecentProjects,
		optionsAllProjects,
		formatMessage,
		labelProjectSelectorRecentProjects,
		labelProjectSelectorProjects,
	]);

	// Keep local state in sync with controlled value
	useEffect(() => {
		if (controlledValue !== undefined) setLocalValue(controlledValue);
	}, [controlledValue]);

	const handleChange = (selectedOption: ValueType<ProjectOption>) => {
		if (!selectedOption || !selectedOption.value) {
			return;
		}

		if (selectedOption.value === localValue) {
			return;
		}

		const newValue = selectedOption ? selectedOption.value : null;
		setLocalValue(newValue);
		onChange?.(newValue);
	};

	// Debounce input change and let consumer set the state
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const handleInputChange = useCallback(
		(query: string) => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
			debounceTimeoutRef.current = setTimeout(() => {
				if (onInputChange) {
					onInputChange(query);
				}
			}, 300);
		},
		[onInputChange],
	);

	if (!fg('jira_ai_decouple_issue_create_fields')) {
		// Old Sweet State logic

		return (
			<Field<ValueType<ProjectOption>>
				name="projects"
				// TODO: Remove formatMessage once we cleanup jira_ai_decouple_issue_create_fields FF
				label={props.label ?? formatMessage(messages.bulkConfigureFormProject)}
				testId="bulk-create-project-field"
			>
				{({ fieldProps: { id, ...rest } }) => (
					<Fragment>
						<Select<ProjectOption>
							inputId={id}
							{...rest}
							isDisabled={isConfigureLoading || !selectedValueSweetState}
							isLoading={isConfigureLoading}
							onInputChange={onInputChangeSweetState}
							value={selectedValueSweetState}
							options={optionsSweetState}
							onChange={onChangeSweetState}
							components={{
								Option: ProjectTypeOption,
								SingleValue: ProjectTypeSingleValue,
							}}
						/>
					</Fragment>
				)}
			</Field>
		);
	}

	return (
		<Field<ValueType<ProjectOption>>
			name="projects"
			label={formatMessage(messages.bulkConfigureFormProject)}
			testId="bulk-create-project-field"
		>
			{({ fieldProps: { id, ...rest } }) => (
				<Fragment>
					<Select<ProjectOption>
						inputId={id}
						{...rest}
						value={selectedValue}
						options={groupedOptions}
						onInputChange={handleInputChange}
						onChange={handleChange}
						isLoading={isLoading}
						isDisabled={isDisabled || isLoading || !selectedValue}
						components={{
							Option: ProjectTypeOption,
							SingleValue: ProjectTypeSingleValue,
						}}
					/>
				</Fragment>
			)}
		</Field>
	);
};
